import type { Tour } from "./types";

/**
 * Curate tours — one per primary page. Each tour articulates how Curate
 * turns a sprawling Cognos content store into a reviewed cleanup plan.
 *
 * Naming: `curate.<page>.v<n>`. Bump v1→v2 on content change.
 */

const NAV = (path: string) => `nav[data-tour-id="nav"] a[href="${path}"]`;

export const plansTour: Tour = {
  id: "curate.plans.v1",
  title: "Welcome to Curate",
  subtitle: "BI cleanup, recommended and reviewed.",
  steps: [
    {
      placement: "center",
      title: "Curate finds the dead weight in Cognos",
      body:
        "Reports nobody runs, dashboards that duplicate other dashboards, datasources owned by people who left two years ago. Curate scans your content store and proposes a Plan — a reviewable, reversible set of cleanup actions.",
    },
    {
      target: '[data-tour-id="plans-create"]',
      placement: "left",
      title: "Start a new plan",
      body:
        "A plan is a snapshot of cleanup recommendations: archive stale content, deduplicate near-identical reports, transfer ownership for orphaned items. You review every action before anything changes in Cognos.",
    },
    {
      target: '[data-tour-id="plans-list"]',
      placement: "top",
      title: "Plans by lifecycle",
      body:
        "Each plan moves through DRAFT → REVIEWING → APPROVED → APPLIED → CLOSED. Filters help you focus on what's awaiting review or what's already in flight.",
    },
    {
      target: NAV("/upgrade-readiness"),
      placement: "bottom",
      title: "Upgrade Readiness",
      body:
        "Heading toward Cognos 12 (or off Cognos entirely)? The Upgrade Readiness page surfaces every blocker — deprecated features, broken lineage, version-incompatible reports — so cleanup has a destination, not just a delete list.",
    },
    {
      placement: "center",
      title: "Always replayable",
      body:
        "Every Curate action is audit-logged with the user, reasoning, and a 30-day rollback window. The help icon (?) replays this tour anytime.",
    },
  ],
};

export const planDetailTour: Tour = {
  id: "curate.planDetail.v1",
  title: "Inside a plan",
  subtitle: "Review, approve, and apply cleanup actions.",
  steps: [
    {
      placement: "center",
      title: "A plan is a reviewable diff",
      body:
        "Each plan item proposes one action: archive, delete, deduplicate, transfer ownership. Each item carries the reasoning Curate used to flag it — so review is a conversation, not a leap of faith.",
    },
    {
      target: '[data-tour-id="plan-items"]',
      placement: "top",
      title: "Approve item-by-item",
      body:
        "Sort by risk score, group by action type, drill into the underlying Cognos object. Approve the safe wins, defer the ambiguous ones, reject the false positives — all without leaving the page.",
    },
    {
      target: '[data-tour-id="plan-apply"]',
      placement: "left",
      title: "Apply with rollback",
      body:
        "When a plan is approved, Apply runs the actions through the platform's safe-action layer. Every change is logged with a rollback token; reverting is one click for 30 days.",
    },
  ],
};

export const upgradeReadinessTour: Tour = {
  id: "curate.upgrade.v1",
  title: "Upgrade Readiness",
  subtitle: "Find every blocker before you migrate.",
  steps: [
    {
      placement: "center",
      title: "Migrations live or die on hidden blockers",
      body:
        "Cognos 11 → 12 (or to Power BI / Tableau via Bridge) trips on deprecated visualisations, broken cross-package references, and reports nobody can reproduce. Curate inventories every blocker before you start.",
    },
    {
      target: '[data-tour-id="upgrade-summary"]',
      placement: "bottom",
      title: "At-a-glance readiness",
      body:
        "The summary tile shows what percent of your content is ready to migrate cleanly, by category. Scan red → orange → green to focus the conversation with stakeholders.",
    },
    {
      target: '[data-tour-id="upgrade-blockers"]',
      placement: "top",
      title: "Per-blocker drill-down",
      body:
        "Click any blocker category to see the affected reports, their owners, and Curate's recommendation. Many become Plan items in one click — readiness directly feeds cleanup.",
    },
  ],
};

export const settingsTour: Tour = {
  id: "curate.settings.v1",
  title: "Settings",
  subtitle: "Provider, semantic layer, and feature flags.",
  steps: [
    {
      placement: "center",
      title: "What Curate believes about itself",
      body:
        "Settings shows the currently-bound LLM provider, the semantic layer Curate uses for content classification, and the feature flags governing experimental capabilities. Most knobs are env-bound; this view is read-only.",
    },
    {
      target: '[data-tour-id="settings-llm"]',
      placement: "right",
      title: "LLM provider",
      body:
        "Curate uses the LLM for classification (similar/duplicate detection, owner inference) and for plan-item explanations. Switching providers takes a bundle restart with new env vars.",
    },
  ],
};

export function tourForPath(pathname: string): Tour | undefined {
  if (pathname.startsWith("/plans/")) return planDetailTour;
  if (pathname.startsWith("/plans")) return plansTour;
  if (pathname.startsWith("/upgrade-readiness")) return upgradeReadinessTour;
  if (pathname.startsWith("/settings")) return settingsTour;
  return undefined;
}
